import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plane } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.full_name}`);
      if (data.user.role === "leader") navigate("/leader");
      else if (!data.user.handshake_signed) navigate("/handshake");
      else navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B14] flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/37106376/pexels-photo-37106376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Jet"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#060B14] via-[#060B14]/70 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" data-testid="login-back-home-link"><Logo /></Link>
          <div>
            <div className="hud-label mb-3">Mission Control</div>
            <h2 className="font-display text-3xl text-white max-w-md leading-tight">
              Welcome back, pilot.<br />
              <span className="text-gold italic font-light">Your squadron awaits.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-12"><Link to="/"><Logo /></Link></div>

          <div className="flex items-center gap-3 mb-8">
            <div className="hud-line w-10" />
            <span className="hud-label">Pilot Sign-In</span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl text-white mb-3 font-light">
            Resume your <span className="text-gold italic">ascent</span>
          </h1>
          <p className="text-slate-400 text-sm mb-10">Enter your credentials to access mission control.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              testId="login-email-input"
              required
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              testId="login-password-input"
              required
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="btn-gold w-full py-3.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Plane size={16} />
              {loading ? "Authenticating..." : "Enter Academy"}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-white/10 text-sm text-slate-400">
            New recruit?{" "}
            <Link to="/" className="text-gold hover:underline" data-testid="login-to-landing-link">
              Get your Pilot License
            </Link>
            <span className="mx-3 text-slate-700">·</span>
            <Link to="/leader-signup" className="text-gold hover:underline" data-testid="login-to-leader-signup-link">
              Leader sign-up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, type, value, onChange, testId, required }) => (
  <label className="block">
    <div className="hud-label text-[10px] mb-2">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      required={required}
      className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-gold transition-colors"
    />
  </label>
);
